import { useQuery } from '@tanstack/react-query';
import { sortBy } from 'lodash';

import { getSankeyTitle } from 'diagram/title';
import { sankeyToForm } from 'utils/form';

import { useApiContext } from './contexts/apiContext';
import { Sankey } from './generated-api';
import { useCurrentRouteSankeyId } from './utils/routes';
import { useCurrentUser } from './utils/user';

/*
 * Queries and mutations
 */

export const useCurrentUserQuery = () => {
  const { api } = useApiContext();

  return useQuery({
    queryKey: ['current-user'],
    queryFn: async () => {
      try {
        return (await api().currentUser.retrieve()).data;
      } catch (error) {
        throw Error('Error fetching current user');
      }
    },
  });
};

export const useCurrentSankeyQuery = (onFetched: (sankey: Sankey) => void) => {
  const id = useCurrentRouteSankeyId();
  const { user } = useCurrentUser();
  const { api } = useApiContext();

  return useQuery({
    queryKey: ['sankey', id],
    queryFn: async () => {
      try {
        const { data: sankey } = await api().sankeys.retrieve({ id });
        onFetched(sankey);
        return sankey;
      } catch (error) {
        throw Error('Error fetching current Sankey');
      }
    },
    // Keep showing the current Sankey data while refetching for smoother UI transitions.
    keepPreviousData: true,
    // Don't cache the current Sankey data to avoid showing stale data.
    cacheTime: 0,
    // Do not refetch the diagram if the user is not able to update it (since
    // that refetch would destroy all the work user did on this diagram).
    refetchOnWindowFocus: !!user?.can_update_sankeys,
  });
};

export const useTeamSankeysQuery = () => {
  const { api } = useApiContext();
  const { user } = useCurrentUser();

  const teamId = user?.default_team?.id;

  return useQuery({
    queryKey: ['team-sankeys'],
    queryFn: async () => {
      try {
        const { data } = await api().sankeys.list({ teamId });
        return sortBy(data, 'created_at').reverse();
      } catch (error) {
        throw Error(`Error fetching team Sankeys`);
      }
    },
    enabled: !!user,
  });
};

export const useTemplateSankeysWithSVGQuery = (enabled: boolean) => {
  const { api } = useApiContext();

  return useQuery({
    queryKey: ['template-sankeys-with-svg'],
    queryFn: async () => {
      try {
        const { data } = await api().sankeys.list(
          { isTemplate: true },
          {
            params: { simple: false },
          }
        );
        // Sort templates by their name but always put income templates first (like Django does in get_template_sankeys)
        return sortBy(data, (template) => {
          const priority = template.statement_type === 'income' ? 0 : 1;
          return `${priority}-${getSankeyTitle(sankeyToForm(template))}`;
        });
      } catch (error) {
        throw Error('Error fetching template Sankeys');
      }
    },
    enabled,
  });
};
